import * as React from "react";
import { clsx } from "@plans/appkit";
import { XCloseIcon } from "@plans/icons/untitled-ui/XCloseIcon";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import { useToast } from "~/lib/ui/ToastContext";
import {
	A_VERY_VERY_VERY_VERY_LARGE_NUMBER,
	DEFAULT_TOAST_LIFETIME,
} from "~/ui/constants";
import type { ToastVariant } from "~/ui/types";
import { Button } from "../atoms/Button";
import { Markdown } from "../atoms/Markdown";

const variantClassNamesMap: Record<ToastVariant, string> = {
	warning: "text-white bg-accent-error",
	success: "text-white bg-accent-success",
	info: "text-white bg-background-inverse",
	offline: "text-white bg-background-inverse",
};

type ManagedToastsProps = {
	className?: string;
};

export function ManagedToasts({ className }: ManagedToastsProps) {
	const { state } = useToast();
	const { setToastLifetime, clearToast } = useToast();

	const onCloseButtonClick = (toastId: number) => {
		clearToast(toastId);
	};

	return (
		<div
			className={clsx(
				"z-(--z-toast) pointer-events-none fixed bottom-0 left-0 flex flex-col-reverse gap-y-3 p-6",
				className,
			)}
		>
			<TransitionGroup component={null}>
				{state.map((toast, index) => {
					const itemRef = React.createRef<HTMLButtonElement>();

					return (
						<CSSTransition
							key={toast.id}
							timeout={500}
							unmountOnExit={true}
							in={false}
							classNames={{
								enter: clsx(
									"-z-10",
									index === 0
										? "translate-y-[calc(100%+_24px)]"
										: "translate-y-[calc(100%+_12px)]",
								),
								enterActive:
									"transition-translate duration-500 !translate-y-0 transform-gpu -z-10",
								exitActive:
									"transition-opacity duration-350 opacity-0 transform-gpu",
							}}
							role="alert"
							nodeRef={itemRef}
						>
							<button
								data-cy={`toast-${toast.variant}`}
								ref={itemRef}
								className={clsx(
									"pointer-events-auto relative flex w-full max-w-[360px] items-center gap-x-4 rounded-sm px-6 py-4 text-left",
									variantClassNamesMap[toast.variant],
								)}
								onClick={toast.onClick}
								onMouseEnter={() =>
									setToastLifetime(toast.id, A_VERY_VERY_VERY_VERY_LARGE_NUMBER)
								}
								onMouseLeave={() =>
									setToastLifetime(
										toast.id,
										toast.lifetime ?? DEFAULT_TOAST_LIFETIME,
									)
								}
							>
								{toast.icon && (
									<div className="-ml- [&_svg]:h-7 [&_svg]:w-7">
										{toast.icon}
									</div>
								)}
								<div className="flex-1">
									<h3
										className="mb-1 text-sm font-semibold"
										data-cy={`toast-${toast.variant}-heading`}
									>
										{toast.heading}
									</h3>
									{toast.body && (
										<Markdown className="text-xs">{toast.body}</Markdown>
									)}
								</div>
								{toast.variant !== "offline" && (
									<Button
										className="absolute right-2.5 top-2.5 z-10 text-current hover:text-current active:text-current active:opacity-[0.7]"
										type="button"
										variant="text"
										isIconOnly={true}
										onClick={() => onCloseButtonClick(toast.id)}
									>
										<XCloseIcon className="h-5 w-5" />
									</Button>
								)}
							</button>
						</CSSTransition>
					);
				})}
			</TransitionGroup>
		</div>
	);
}
